import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Button, Grid, SvgIcon, Tooltip } from '@mui/material';
import { Search } from '@mui/icons-material';
import ImageNotSupported from '@mui/icons-material/ImageNotSupported';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ContentCopy from '@mui/icons-material/ContentCopy';
import AccountTree from '@mui/icons-material/AccountTree';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { client, MarketCategory, MarketCategoryFilterOptions } from '../../../../app/services/api/orderManagementClient';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableColumn, TableSectionProps } from '../../../../components/shared/tables/TableSection';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import type { ListPageHeadline } from '../../../../components/page-templates/list-page/ListPageTemplate';
import { getCategoryIcon } from '../../../Storefront/components/CategorySidebar';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';
import { UserContext } from '../../../../components/shared/useUser';

type Props = {
  onCreateNew: () => void;
  // eslint-disable-next-line no-unused-vars
  onFilterChange?: (instance: string, searchTerm?: string) => void;
  initialInstance?: string;
  initialSearchTerm?: string;
  /** Splits filters, tiles and table so the caller can slot them into ListPageTemplate. */
  // eslint-disable-next-line no-unused-vars
  children: (parts: {
    filterBar: ReactNode;
    headlineContent: ListPageHeadline;
    table: TableSectionProps<MarketCategory>;
    isInitialLoading: boolean;
  }) => ReactNode;
};

const truncateText = (text: string, maxLength: number = 30) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const CategoriesListPageContent = ({ onCreateNew, onFilterChange, initialInstance, initialSearchTerm, children }: Props) => {
  const { t } = useTranslation('categories');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermissionTo } = useContext(UserContext);
  const canEdit = hasPermissionTo(['CategoryUpdate', 'ConfigurationAdmin']);

  const [list, setList] = useState<MarketCategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const { instances, defaultFiltersLoading } = useInstanceMarket();
  const pageSize = 20;

  // Headline tiles summarise the whole Instance scope (not the current search), fetched once per scope change so they don't slow down search/paging.
  const [statsList, setStatsList] = useState<MarketCategory[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedInstance) {
      setStatsList([]);
      return;
    }

    setIsStatsLoading(true);
    client.getMarketCategories({
      companyId: selectedInstance === 'all' ? undefined : selectedInstance,
      pageNumber: 0,
      pageSize: 2147483647,
      sort: '',
      direction: 'asc',
    })
      .then((result) => {
        if (result.success && result.data) {
          setStatsList(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching category stats:', error))
      .finally(() => setIsStatsLoading(false));
  }, [selectedInstance]);

  const missingIconCount = useMemo(() => (
    statsList.filter((category) => !category.iconKey).length
  ), [statsList]);

  const totalCategoriesCount = statsList.length;

  const subCategoriesCount = useMemo(() => (
    statsList.filter((category) => category.parentId > 0).length
  ), [statsList]);

  const orphanedSubCategoriesCount = useMemo(() => {
    const categoryIds = new Set(statsList.map((category) => category.categoryId));
    return statsList.filter((category) => category.parentId > 0 && !categoryIds.has(category.parentId)).length;
  }, [statsList]);

  const orphanedSubCategoriesRatio = `${orphanedSubCategoriesCount}/${subCategoriesCount}`;

  const duplicateNameCount = useMemo(() => {
    const nameCounts = new Map<string, number>();
    statsList.forEach((category) => {
      const key = category.name?.trim().toLowerCase();
      if (!key) return;
      nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
    });
    return statsList.filter((category) => {
      const key = category.name?.trim().toLowerCase();
      return !!key && (nameCounts.get(key) ?? 0) > 1;
    }).length;
  }, [statsList]);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const options: MarketCategoryFilterOptions = {
        companyId: selectedInstance === 'all' ? undefined : (selectedInstance || undefined),
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
        sort: '',
        direction: 'asc',
      };
      const result = await client.getMarketCategories(options);
      if (result.success && result.data) {
        setList(result.data.items);
        setTotalCount(result.data.totalCount);
      } else {
        setList([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
  }, [initialInstance, initialSearchTerm]);

  useEffect(() => {
    if (selectedInstance || selectedInstance === 'all') {
      fetchCategories();
    }
  }, [currentPage, searchTerm, selectedInstance]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, searchInput);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setCurrentPage(0);
    onFilterChange?.(event.target.value, searchInput);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('all');
    setCurrentPage(0);
    onFilterChange?.('all', '');
  };

  const handleRowClick = (category: MarketCategory) => {
    if (!canEdit) return;
    const params = new URLSearchParams();
    const companyId = searchParams.get('companyId');
    const term = searchParams.get('searchTerm');
    if (companyId) params.set('companyId', companyId);
    if (term) params.set('searchTerm', term);
    navigate(`/procurement/categories/edit/${category.categoryId}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const columns: TableColumn<MarketCategory>[] = [
    {
      id: 'name',
      label: t('categoryName'),
      render: (row) => (
        <Tooltip title={row.name}
          placement="top">
          <span>{truncateText(row.name)}</span>
        </Tooltip>
      ),
    },
    {
      id: 'parentName',
      label: t('parentCategory'),
      render: (row) => (
        <Tooltip title={row.parentName || t('none')}
          placement="top">
          <span>{truncateText(row.parentName || t('none'))}</span>
        </Tooltip>
      ),
    },
    {
      id: 'instanceName',
      label: t('instanceName'),
      render: (row) => (
        <Tooltip title={row.instanceName}
          placement="top">
          <span>{truncateText(row.instanceName)}</span>
        </Tooltip>
      ),
    },
    {
      id: 'icon',
      label: t('icon'),
      render: (row) => getCategoryIcon(row.iconKey),
    },
  ];

  const table: TableSectionProps<MarketCategory> = {
    title: t('categories'),
    columns,
    rows: list,
    getRowId: (row) => `${row.categoryId}-${row.companyId}`,
    onRowClick: canEdit ? handleRowClick : undefined,
    recordCount: totalCount,
    recordCountLabel: t('categories'),
    loading: isLoading,
    emptyContent: (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('categories')}
        searchTerm={searchInput}
        onClear={handleClear}
        onAdd={onCreateNew}
        onAddIsCreating={false}
        addItemText={t('procurementCreateNew', 'Create New Procurement Category')}
        withPaginationShown={false}
      />
    ),
    page: currentPage,
    maxPages: Math.max(1, Math.ceil(totalCount / pageSize)),
    onPageChange: handlePageChange,
  };

  const filterBar = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={4}
        sm={6}
        xs={12}>
        <TextField
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          {t('clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        lg={4}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByInstance')}
          </InputLabel>
          <Select
            value={selectedInstance === 'all' || instances.some(m => m.id?.toString() == selectedInstance) ? selectedInstance : ''}
            onChange={handleInstanceChange}
            label={t('filterByInstance')}
            displayEmpty
            notched
            renderValue={(value) => {
              if (value === '' || value === 'all') {
                return <span>{t('allInstances')}</span>;
              }

              return instances?.find(i => i.id != null && i.id.toString() === value)?.name || '';
            }}
          >
            <MenuItem value="all">
              {t('allInstances')}
            </MenuItem>
            {instances
              .filter((instance) => instance.id != null)
              .map((instance) => (
                <MenuItem
                  key={instance.id}
                  value={String(instance.id)}
                >
                  {instance.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const headlineContent: ListPageHeadline = {
    insights: [
      <InsightTile
        key="totalCategories"
        label={t('categories')}
        value={totalCategoriesCount}
        description={t('procurementTileTotalCategoriesDescription', 'All categories in the selected instance scope')}
        icon={<AccountTree />}
        variant="secondary"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="duplicateNames"
        label={t('procurementTileDuplicateNames', 'Possible Duplicate Names')}
        value={duplicateNameCount}
        description={t('procurementTileDuplicateNamesDescription', 'Categories sharing the same name as another category')}
        icon={<ContentCopy />}
        variant="primary"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="orphanedSubCategories"
        label={t('procurementTileOrphanedSubCategories', 'Sub-Categories Without Parent')}
        value={orphanedSubCategoriesRatio}
        description={t('procurementTileOrphanedSubCategoriesDescription', 'Sub-categories without parent out of total sub-categories')}
        icon={<ErrorOutline />}
        variant="error"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="missingIcon"
        label={t('procurementTileMissingIcon', 'Missing Icon')}
        value={missingIconCount}
        description={t('procurementTileMissingIconDescription', 'Categories with no icon set')}
        icon={<ImageNotSupported />}
        variant="warning"
        loading={isStatsLoading}
      />,
    ],
  };

  // Still resolving the default instance before the first fetch even starts - keep showing the skeleton, not a zero-data flash.
  return <>{children({ filterBar, headlineContent, table, isInitialLoading: (defaultFiltersLoading || isLoading) && !hasLoadedOnce })}</>;
};

export default CategoriesListPageContent;
